import { Button }         from '@/components/ui/button';
import { Field }          from '@/components/ui/field';
import AddressModel       from '@/models/services/fondations/AddressModel';
import { Box }            from '@chakra-ui/react';
import { Input }          from '@chakra-ui/react';
import { NativeSelect }   from '@chakra-ui/react';
import { Stack }          from '@chakra-ui/react';
import { Text }           from '@chakra-ui/react';
import countries          from 'i18n-iso-countries';
import enLocale           from 'i18n-iso-countries/langs/en.json';
import frLocale           from 'i18n-iso-countries/langs/fr.json';
import { useForm }        from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toaster }        from '@/components/ui/toaster';

countries.registerLocale(frLocale);
countries.registerLocale(enLocale);

interface IEntityAddressFormProps {
	address?:   AddressModel;
	entityUrn:  string;
	onSuccess:  () => Promise<void>;
	onClose:    () => void;
}

interface FormValues {
	addressLine:    string;
	addressLineBis: string;
	zipcode:        string;
	city:           string;
	country:        string;
}

const EntityAddressForm = ({ address, entityUrn, onSuccess, onClose }: IEntityAddressFormProps) => {
	const { t, i18n } = useTranslation();
	const isEditing = !!address?.id;

	// Normalise "fr-FR" → "fr", fallback sur "en"
	const lang = i18n.language.slice(0, 2);
	const countryOptions = Object.entries(countries.getNames(lang, { select: 'official' }))
		.sort(([, a], [, b]) => a.localeCompare(b, lang));

	const { formState: { errors, isSubmitting }, handleSubmit, register } = useForm<FormValues>({
		defaultValues: {
			addressLine:    address?.addressLine    || '',
			addressLineBis: address?.addressLineBis || '',
			zipcode:        address?.zipcode        || '',
			city:           address?.city           || '',
			country:        address?.country        || '',
		},
	});

	const onSubmit = handleSubmit(async (data) => {
		try {
			const model = address?.id ? address : new AddressModel();
			await model.patch({
				// L'entité n'est envoyée qu'à la création — en édition elle est déjà liée
				...(!address?.id ? { entity: entityUrn } : {}),
				addressLine:    data.addressLine,
				addressLineBis: data.addressLineBis || undefined,
				zipcode:        data.zipcode,
				city:           data.city,
				country:        data.country,
			});
			toaster.create({
				title: t(isEditing ? 'pages.entity.addresses.modals.edit.messages.success' : 'pages.entity.addresses.modals.add.messages.success'),
				type:  'success',
			});
			await onSuccess();
		} catch {
			toaster.create({
				title: t(isEditing ? 'pages.entity.addresses.modals.edit.messages.error' : 'pages.entity.addresses.modals.add.messages.error'),
				type:  'error',
			});
		}
	});

	return (
		<form onSubmit={onSubmit}>
			<Stack gap="20px" marginTop="20px">
				<Field
					label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.entity.addresses.forms.addressLine')}</Text>}
					invalid={!!errors.addressLine}
					required
				>
					<Input
						placeholder={t('pages.entity.addresses.forms.addressLine')}
						{...register('addressLine', { required: true })}
					/>
				</Field>

				<Field
					label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.entity.addresses.forms.country')}</Text>}
					invalid={!!errors.country}
					required
				>
					<NativeSelect.Root size="sm" width="100%">
						<NativeSelect.Field {...register('country', { required: true })}>
							<option value="">{t('pages.entity.addresses.forms.country')}</option>
							{countryOptions.map(([code, name]) => (
								<option key={code} value={code}>{name}</option>
							))}
						</NativeSelect.Field>
						<NativeSelect.Indicator />
					</NativeSelect.Root>
				</Field>

				<Field
					label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.entity.addresses.forms.addressLineBis')}</Text>}
				>
					<Input
						placeholder={t('pages.entity.addresses.forms.addressLineBis')}
						{...register('addressLineBis')}
					/>
				</Field>

				<Box display="grid" gridTemplateColumns="1fr 1fr" gap="15px">
					<Field
						label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.entity.addresses.forms.zipcode')}</Text>}
						invalid={!!errors.zipcode}
						required
					>
						<Input
							placeholder={t('pages.entity.addresses.forms.zipcode')}
							{...register('zipcode', { required: true })}
						/>
					</Field>

					<Field
						label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.entity.addresses.forms.city')}</Text>}
						invalid={!!errors.city}
						required
					>
						<Input
							placeholder={t('pages.entity.addresses.forms.city')}
							{...register('city', { required: true })}
						/>
					</Field>
				</Box>
			</Stack>

			<Box display="flex" gap="10px" marginTop="30px">
				<Button variant="outline" onClick={onClose} flex="1">
					{t('app.actions.cancel')}
				</Button>
				<Button variant="solid" type="submit" loading={isSubmitting} flex="1">
					{t(isEditing ? 'pages.entity.addresses.actions.edit' : 'pages.entity.addresses.actions.add.label')}
				</Button>
			</Box>
		</form>
	);
};

export default EntityAddressForm;
